import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import AuthPage from './pages/AuthPage';
import OptimizationTypePage from './pages/OptimizationTypePage';
import StorefrontFormPage from './pages/StorefrontFormPage';
import StorefrontReviewPage from './pages/StorefrontReviewPage';
import StorefrontSubmissionSuccessfulPage from './pages/StorefrontSubmissionSuccessfulPage';
import ListingSelectionPage from './pages/ListingSelectionPage';
import ListingOnboardingForm from './pages/ListingOnboardingForm';
import CreativeBriefPage from './pages/CreativeBriefPage';
import SubmissionSuccessfulPage from './pages/SubmissionSuccessfulPage';
import ResearchAgentDashboard from './pages/ResearchAgentDashboard';
import ResearchLandingPage from './pages/ResearchLandingPage';
import CopyAgentPage from './pages/CopyAgentPage';
import TestResearchPage from './pages/TestResearchPage';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedLayout from './components/ProtectedLayout';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import EmailVerification from './components/EmailVerification';
import OAuthCallbackPage from './pages/OAuthCallbackPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordForm />} />
          <Route path="/reset-password" element={<ResetPasswordForm />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
          
          {/* Protected Routes - Require Authentication */}
          <Route path="/optimize" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <OptimizationTypePage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/research-landing" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <ResearchLandingPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/research-agent-dashboard" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <ResearchAgentDashboard />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/research-agent-dashboard/:projectId" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <ResearchAgentDashboard />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/test-research/:projectId" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <TestResearchPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/listing-selection" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <ListingSelectionPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/listing-onboarding" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <ListingOnboardingForm />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/storefront-form" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <StorefrontFormPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/storefront-review" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <StorefrontReviewPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/storefront-submission-successful" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <StorefrontSubmissionSuccessfulPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/creative-brief" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <CreativeBriefPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/creative-brief/:projectId" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <CreativeBriefPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/submission-successful" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <SubmissionSuccessfulPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/submission-successful/:projectId" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <SubmissionSuccessfulPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          <Route path="/copy/:projectId" element={
            <ProtectedRoute>
              <ProtectedLayout>
                <CopyAgentPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } />
          
          {/* Legacy auth route for backward compatibility */}
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          
          {/* Default redirect to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      
      {/* Global Toaster */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
};

export default App; 